<template>
  <div>
    <submit-section-modal
      v-if="showModal"
      @close-modal="closeModal"
      @submit-section="handleSubmitSection"
    >
      <p>
        Are you sure you wish to continue?<br />
        You cannot go back.
      </p>
    </submit-section-modal>
    <div class="section-review-border">
      <section-review-header
        :is-simulation-complete="isSimulationComplete"
        :correct-count="groupingTally.correct"
        :total-questions="groupingFlashcards.length"
        :formatted-time="formattedTime(timeLeftInSeconds)"
        @return-to-performance="goToPerformance"
      />
      <data-loader>
        <div
          class="section-review-content"
        >
          <section-review-top-matter
            :title="`${groupingName.toUpperCase()} REVIEW`"
            :section-title="`${groupingName} questions`"
            :flashcard-list-statuses="groupingFlashcardListStatuses"
            :tally="groupingTally"
            :is-simulation-complete="isSimulationComplete"
          >
            <div><strong>Below is a summary of your answers.</strong></div>
            <div>The buttons in the lower left corner have three functions:</div>
            <ol>
              <li>Review all of your questions answered, incomplete and flagged.</li>
              <li>Review questions that are incomplete.</li>
              <li>Review questions that are flagged.</li>
            </ol>
          </section-review-top-matter>
          <section-review-column-container
            :number-of-columns="3"
            :flashcard-list-statuses="groupingFlashcardListStatuses"
            :is-simulation-complete="isSimulationComplete"
            :show-section-name="true"
            @navigate="navigate"
          />
        </div>
      </data-loader>
      <div class="section-review-footer">
        <section-review-footer
          :is-simulation-complete="isSimulationComplete"
          @review="startReview"
          @submit-section="openModal"
          @return-to-performance="goToPerformance"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SectionReviewFooter from '@components/sims/sectionReview/SectionReviewFooter.vue';
import SectionReviewHeader from '@components/sims/sectionReview/SectionReviewHeader.vue';
import SectionReviewTopMatter from '@components/sims/sectionReview/SectionReviewTopMatter.vue';
import SectionReviewColumnContainer from '@components/sims/sectionReview/SectionReviewColumnContainer.vue';
import SubmitSectionModal from '@components/sims/sectionReview/SubmitSectionModal.vue';
import WuxSimulationsMixin from '@/mixins/wux-simulations';
import BlueprintMixin from '@/mixins/blueprint';
import DataLoader from '@/components/sims/DataLoader.vue';

export default {
  components: {
    DataLoader,
    SectionReviewFooter,
    SectionReviewHeader,
    SectionReviewTopMatter,
    SectionReviewColumnContainer,
    SubmitSectionModal,
  },
  mixins: [WuxSimulationsMixin, BlueprintMixin],
  data() {
    return {
      showModal: false,
      timeLeftInSeconds: null,
      timerInterval: null,
    };
  },
  computed: {
    // All grouping-related computed properties are now in BlueprintMixin
  },
  watch: {
    timeLeftInSeconds(currentValue) {
      if (currentValue < 0) {
        this.handleSubmitSection();
      }
    },
  },
  mounted() {
    if (this.isSimulationComplete) {
      return;
    }

    this.timerInterval = setInterval(() => {
      try {
        this.timerTick(this.inProgressStatus, this.section.timeInMinutes);
      }
      catch {
        clearInterval(this.timerInterval);
      }
    }, 250);
  },
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    handleSubmitSection() {
      if (this.isSimulationComplete) {
        return;
      }

      clearInterval(this.timerInterval);
      this.closeModal();
      this.submitSection();
    },
    goToPerformance() {
      this.$router.push({
        name: 'sims#performance',
        params: {
          simId: this.simId,
        },
      });
    },
    navigate(flashcardId, filter) {
      return this.$router.push({
        name: 'simsGroupingFlashcard#review',
        params: {
          simId: this.simId,
          groupingId: this.blueprintId,
          flashcardId: flashcardId,
        },
        query: {
          filter,
        },
      });
    },
    startReview(filter) {
      let flashcardId = this.groupingFlashcardListStatuses[0].id;

      if (filter === 'incomplete') {
        this.$store.dispatch('flashcardSet/setIds',
          this.groupingFlashcardListStatuses.filter(status => !status.isAnswered).map(status => status.id),
        );
        flashcardId = this.$store.getters['flashcardSet/ids'][0];
      }

      if (filter === 'flagged') {
        this.$store.dispatch('flashcardSet/setIds',
          this.groupingFlashcardListStatuses.filter(status => status.isBookmarked).map(status => status.id),
        );
        flashcardId = this.$store.getters['flashcardSet/ids'][0];
      }

      if (filter === 'incorrect') {
        this.$store.dispatch('flashcardSet/setIds',
          this.groupingFlashcardListStatuses.filter(
            status => (status.isAnswered && !status.isCorrect) || !status.isAnswered,
          ).map(status => status.id),
        );
        flashcardId = this.$store.getters['flashcardSet/ids'][0];
      }

      if (!flashcardId) return;
      // When falsy, it's important to pass undefined so that the query string will be empty
      this.navigate(flashcardId, filter ? filter : undefined);
    },
  },
};
</script>

<style lang='scss' scoped>
$sim-background-color: #00629E;
$header-height: 4.5rem;
$border-width: 1rem;
$footer-height: 4.5rem;

.section-review-border {
  margin: 0;
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  z-index: 1000;
  background-color: $sim-background-color;

  .section-review-content {
    position: fixed;
    top: $header-height;
    left: $border-width;
    right: $border-width;
    bottom: $footer-height;
    background-color: white;
    overflow: auto;
    padding: 0 1.5rem;
  }

  .section-review-footer {
    position: fixed;
    color: white;
    background-color: $sim-background-color;
    bottom: 0px;
    left: 0px;
    right: 0px;
    height: $footer-height;
  }
}

</style>
